import { useUser } from "@/hooks/AppContext/user.hook";
import { AppContext } from "@/services/AppContext/appContext";
import { OrganizedTimersEvent } from "@/types";
import {
  calculateTimeDifference,
  formatElapsedTime,
  getNewTaskTagByID,
} from "@/utils/utils";
import {
  format,
  isPast,
  isThisYear,
  isToday,
  isTomorrow,
  isYesterday,
  parseISO,
} from "date-fns";
import { User } from "lucide-react";
import React, { ReactNode, useContext, useMemo } from "react";
import SingleTag from "../Tags/tag.component";

type SingleLineTimerMobileProps = {
  timer: OrganizedTimersEvent;
  type: "older" | "Today" | "This Week" | "No Events";
};

const SingleLineTimerMobile: React.FC<SingleLineTimerMobileProps> = ({
  timer,
  type,
}) => {
  const { getUserFullNameByID, getUserProfileImageByID } = useUser();
  const { userTags, fetchPageTimers, fetchEveryoneTimers, runningTimer } =
    useContext(AppContext);

  const { task_name, name, tags, user_id } = timer.timer;
  const { event_end, event_start } = timer;

  function formatCellDate(date: string | null): ReactNode {
    try {
      if (!date) return <div className="">No End Time</div>;

      const parsedDate = parseISO(date);
      let dateShown;
      let colorClass = "";
      if (isToday(parsedDate)) {
        colorClass = "text-green-400";
        dateShown = `Today ${format(parsedDate, "HH:mm:ss")}`;
      } else if (isTomorrow(parsedDate)) {
        colorClass = "text-indigo-400";
        dateShown = `Tomorrow ${format(parsedDate, "HH:mm:ss")}`;
      } else if (isPast(parsedDate)) {
        if (isYesterday(parsedDate)) {
          colorClass = "text-indigo-400";
          dateShown = `Yesterday ${format(parsedDate, "HH:mm:ss")}`;
        }
      }

      if (isThisYear(parsedDate)) {
        dateShown = dateShown ?? format(parsedDate, "yyy/MM/dd HH:mm:ss");
      } else {
        dateShown = dateShown ?? format(parsedDate, "yyy/MM/dd HH:mm:ss");
      }

      return <div className={`${colorClass}`}>{dateShown}</div>;
    } catch (error) {
      console.error("Error formatting date:", error);
      console.log("Date string:", date);
      return <div className="">Invalid Date</div>;
    }
  }

  const assigneeImage = useMemo(() => {
    return user_id ? getUserProfileImageByID(user_id) : null;
  }, [user_id, getUserProfileImageByID]);

  const assigneeName = useMemo(() => {
    return user_id ? getUserFullNameByID(user_id) : null;
  }, [user_id, getUserFullNameByID]);

  return (
    <div className="w-full bg-accent text-accent-foreground p-2 rounded flex flex-col gap-2 overflow-hidden">
      <div className="flex flex-row justify-between w-full overflow-hidden gap-4">
        <div className="truncate text-base font-bold shrink-1 text-ellipsis whitespace-nowrap">
          {name}
        </div>
        <div className="font-bold text-primary shrink-0 text-ellipsis whitespace-nowrap">
          {event_start && event_end
            ? formatElapsedTime(calculateTimeDifference(event_start, event_end))
            : ""}
        </div>
      </div>
      <div className="flex flex-row gap-1 items-center overflow-hidden">
        <div className="truncate text-ellipsis whitespace-nowrap">
          {formatCellDate(event_start)}
        </div>
        <div>-</div>
        <div className="truncate text-ellipsis whitespace-nowrap">
          {event_end ? formatCellDate(event_end) : "Ongoing"}
        </div>
      </div>
      <div className="flex gap-1 items-center overflow-hidden">
        <div>
          {assigneeImage ? (
            <img
              className="size-6 rounded-full shrink-0"
              src={assigneeImage}
              alt="Assignee"
            />
          ) : (
            <User className="h-5 w-5 rounded-full bg-primary shrink-0" />
          )}
        </div>
        <div className="truncate text-ellipsis whitespace-nowrap">
          {assigneeName ? <div>{assigneeName}</div> : <div>Not assigned</div>}
        </div>
      </div>
      <div className="w-full truncate text-ellipsis whitespace-nowrap">
        Task: {task_name ? task_name : "No Task"}
      </div>
      {/* tags */}
      <div className="w-full flex gap-1 flex-wrap">
        {tags &&
          tags.length > 0 &&
          tags.map((tag) => {
            const fullTag = getNewTaskTagByID(tag, userTags);
            return (
              fullTag && (
                <div key={fullTag.tag + fullTag.category_id} className="">
                  <SingleTag
                    id={fullTag.tag + fullTag.category_id}
                    color={fullTag.color}
                    name={fullTag.tag}
                    full={true}
                    className="text-xs px-1 py-1 h-auto"
                  />
                </div>
              )
            );
          })}
      </div>
    </div>
  );
};

export default SingleLineTimerMobile;
